import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const Figuren = ({ projectId }) => {
 const [characters, setCharacters] = useState([]);
 const [editingCell, setEditingCell] = useState(null);
 const [newCharacter, setNewCharacter] = useState({
   figur: '',
   kurzbeschreibung: ''
 });

 useEffect(() => {
   const loadSavedData = () => {
     if (projectId) {
       console.log('Loading characters for project:', projectId);
       const savedCharacters = localStorage.getItem(`characters_${projectId}`);
       
       if (savedCharacters) {
         try {
           const parsedCharacters = JSON.parse(savedCharacters);
           if (parsedCharacters && parsedCharacters.length > 0) {
             console.log('Loading characters:', parsedCharacters);
             setCharacters(parsedCharacters);
           }
         } catch (error) {
           console.error('Error loading characters:', error);
         }
       }
     }
   };

   loadSavedData();
 }, [projectId]);

 useEffect(() => {
   const saveData = () => {
     if (projectId && characters && characters.length > 0) {
       console.log('Saving characters:', characters);
       localStorage.setItem(`characters_${projectId}`, JSON.stringify(characters));
     }
   };

   saveData();
 }, [characters, projectId]);

 const handleInputChange = (e) => {
   const { name, value } = e.target;
   setNewCharacter(prev => ({
     ...prev,
     [name]: value
   }));
 };

 const handleCellEdit = (id, field, value) => {
   setCharacters(prev => prev.map(character => 
     character.id === id ? { ...character, [field]: value } : character
   ));
   setEditingCell(null);
 };

 const addCharacter = () => {
   if (newCharacter.figur && newCharacter.kurzbeschreibung) {
     const characterToAdd = { 
       ...newCharacter,
       id: Date.now() 
     };
     
     setCharacters(prev => [...prev, characterToAdd]);
     
     setNewCharacter({
       figur: '',
       kurzbeschreibung: ''
     });
   }
 };

 const deleteCharacter = (id) => {
  if (window.confirm('Möchten Sie diese Figur wirklich löschen?')) {
    setCharacters(prev => {
      const updatedCharacters = prev.filter(character => character.id !== id);

      if (updatedCharacters.length === 0) {
        localStorage.removeItem(`characters_${projectId}`);
      } else {
        localStorage.setItem(`characters_${projectId}`, JSON.stringify(updatedCharacters));
      }

      return updatedCharacters;
    });
  }
};

 const exportToCSV = () => {
   const csv = Papa.unparse(characters);
   const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
   const link = document.createElement('a');
   const url = URL.createObjectURL(blob);
   link.setAttribute('href', url);
   link.setAttribute('download', 'figuren.csv');
   link.style.visibility = 'hidden';
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
 };

 const handleFileUpload = async (e) => {
   const file = e.target.files[0];
   if (!file) return;

   try {
       let data;
       if (file.name.endsWith('.csv')) {
           const text = await file.text();
           const results = Papa.parse(text, {
               header: true,
               skipEmptyLines: true
           });
           data = results.data;
       } else {
           const buffer = await file.arrayBuffer();
           const workbook = XLSX.read(buffer, {
               type: 'array',
               dateNOS: true,
               cellDates: true
           });
           
           const firstSheetName = workbook.SheetNames[0];
           const worksheet = workbook.Sheets[firstSheetName];
           
           data = XLSX.utils.sheet_to_json(worksheet, {
               raw: false
           });
       }

       const newCharacters = data
           .filter(character => character.figur || character.Figur)
           .map(character => ({
               id: Date.now() + Math.random(),
               figur: character.figur || character.Figur || '',
               kurzbeschreibung: character.kurzbeschreibung || character.Kurzbeschreibung || 
                               character.beschreibung || character.Beschreibung || ''
           }));

       setCharacters(prev => [...prev, ...newCharacters]);
       alert(`${newCharacters.length} Figuren wurden erfolgreich importiert.`);

   } catch (error) {
       console.error('Fehler beim Datei-Import:', error);
       alert('Fehler beim Import der Datei. Bitte überprüfen Sie das Format.');
   }

   e.target.value = '';
 };

 return (
   <div className="p-6">
     <h2 className="text-2xl font-bold mb-4">Figuren</h2>
     
     <div className="grid grid-cols-3 gap-4 mb-6">
       <input
         type="text"
         name="figur"
         value={newCharacter.figur}
         onChange={handleInputChange}
         placeholder="Figur"
         className="border p-2"
         required
       />
       <input
         type="text"
         name="kurzbeschreibung"
         value={newCharacter.kurzbeschreibung}
         onChange={handleInputChange}
         placeholder="Kurzbeschreibung"
         className="border p-2"
         required
       />
       <button
         onClick={addCharacter}
         className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
         disabled={!newCharacter.figur || !newCharacter.kurzbeschreibung}
       >
         Figur hinzufügen
       </button>
     </div>

     <div className="mb-4 flex space-x-2">
       <input
         type="file"
         accept=".csv,.xlsx,.xls"
         onChange={handleFileUpload}
         className="hidden"
         id="csvUpload"
       />
       <label 
         htmlFor="csvUpload" 
         className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
       >
         Excel importieren
       </label>
       <button
         onClick={exportToCSV}
         className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
       >
         Als Excel exportieren
       </button>
     </div>

     <div className="overflow-x-auto">
       <table className="w-full border-collapse border">
         <thead>
           <tr className="bg-gray-200">
             <th className="border p-2">Figur</th>
             <th className="border p-2">Kurzbeschreibung</th>
             <th className="border p-2">Aktionen</th>
           </tr>
         </thead>
         <tbody>
           {characters.map((character) => (
             <tr key={character.id}>
               <td 
                 className="border p-2 cursor-pointer" 
                 onClick={() => setEditingCell({ id: character.id, field: 'figur' })}
               >
                 {editingCell?.id === character.id && editingCell?.field === 'figur' ? (
                   <input
                     type="text"
                     value={character.figur}
                     onChange={(e) => handleCellEdit(character.id, 'figur', e.target.value)}
                     onBlur={() => setEditingCell(null)}
                     autoFocus
                     className="w-full"
                   />
                 ) : character.figur}
               </td>
               <td 
                 className="border p-2 cursor-pointer"
                 onClick={() => setEditingCell({ id: character.id, field: 'kurzbeschreibung' })}
               >
                 {editingCell?.id === character.id && editingCell?.field === 'kurzbeschreibung' ? (
                   <input
                     type="text"
                     value={character.kurzbeschreibung}
                     onChange={(e) => handleCellEdit(character.id, 'kurzbeschreibung', e.target.value)}
                     onBlur={() => setEditingCell(null)}
                     autoFocus
                     className="w-full"
                   />
                 ) : character.kurzbeschreibung}
               </td>
               <td className="border p-2">
                 <button
                   onClick={() => deleteCharacter(character.id)}
                   className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                 >
                   Löschen
                 </button>
               </td>
             </tr>
           ))}
         </tbody>
       </table>
     </div>
   </div>
 );
};

export default Figuren;